// Structure and Tailwind classNames ported from the Lovable reference
// implementation (files/snapdraw-design-kit/code/src/routes/index.tsx), now
// built on the shared AuthLayout (also used by SignUpPage/ForgotPasswordPage).
// Retrofitted with real Supabase auth in place of the demo navigate() and a
// real "Forgot password?" link.
// Field validation (required/invalid-email/required-password) ported from
// the kit's 2026-08-22 "form validation & error states" update: validates on
// submit, clears per-field as the user types, renders via the shared
// FieldError component (replaces the old bare destructive-text paragraph,
// which a real accessibility/readability complaint flagged — see
// docs/BACKLOG.md). The real Supabase sign-in error (wrong credentials,
// network failure, etc.) reuses the same FieldError treatment for
// consistency, though the kit itself never modeled that case since its
// forms don't call a real API.
// "Keep me signed in" controls real behavior: checked persists the session
// in localStorage (survives closing the browser), unchecked uses
// sessionStorage (cleared when the tab/browser closes) — see the storage
// adapter in @/lib/supabase. Defaults to checked, matching the app's
// original always-remembered behavior.
// Google/Microsoft sign-in (backlog AUTH-8, 2026-09-04) — previously flagged
// off behind SSO_ENABLED pending a large-customer request; now real,
// wired through supabase.auth.signInWithOAuth(). Supabase's own provider
// slug for Microsoft is "azure" (Azure AD under the hood), not "microsoft".
// signInWithOAuth() redirects the whole page away on success — there's no
// local success path to handle, only a synchronous pre-redirect error (rare:
// misconfigured provider, network failure before the redirect fires).
// Errors store i18n KEYS (not resolved strings) and re-resolve via t() at
// render time, so they stay correct if the user flips the language toggle
// after an error appears (2026-08-22 fix — storing the resolved string froze
// it in whatever language was active when the error was set). The real
// Supabase sign-in error is genuinely untranslatable third-party text (see
// Message's `raw` case in @/lib/i18n/message) — it can't follow the toggle
// because no Spanish version of it exists anywhere in our system.
// Password field has a show/hide toggle (2026-08-22) via the shared
// PasswordInput component.
import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AtSign, Link2Off, ShieldAlert } from "lucide-react";

import { AuthLayout, authFieldClassName, authPrimaryClass } from "@/components/snapdraw/auth-layout";
import { FieldError } from "@/components/snapdraw/form";
import { PasswordInput } from "@/components/snapdraw/password-input";
import { AuthDivider, SsoButtons } from "@/components/snapdraw/sso-buttons";
import { clearOAuthRedirectMarker, markOAuthRedirectStarted } from "@/lib/auth-link-error";
import { safeReturnPath } from "@/lib/authRedirect";
import { supabase, setRememberMe } from "@/lib/supabase";
import { useT } from "@/lib/i18n";
import { resolveMessage, type Message } from "@/lib/i18n/message";
import { cn } from "@/lib/utils";
import "@/styles/tailwind-preview.css";

interface FieldErrorKeys {
  email?: string;
  password?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Set only by requireAccess()'s own redirect (App.tsx) when an
  // unauthenticated visit hit a gated route — e.g. a document link from a
  // notification email. Never present on a direct, unprompted visit to
  // /login.
  const from = safeReturnPath((location.state as { from?: string } | null)?.from);
  const t = useT();
  const [searchParams] = useSearchParams();
  const linkExpired = searchParams.get("link") === "expired";
  const oauthError = searchParams.get("link") === "oauth_error";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [errors, setErrors] = useState<FieldErrorKeys>({});
  const [submitError, setSubmitError] = useState<Message | null>(null);
  const [busy, setBusy] = useState(false);

  function clearError(key: keyof FieldErrorKeys) {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const nextErrors: FieldErrorKeys = {};
    if (!email.trim()) nextErrors.email = "auth.error.required";
    else if (!email.includes("@")) nextErrors.email = "auth.error.email";
    if (!password.trim()) nextErrors.password = "auth.error.password";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setBusy(true);
    try {
      setRememberMe(remember);
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;
      navigate(from ?? "/");
    } catch (err) {
      // Supabase's own message here is stable ("Invalid login credentials")
      // but English-only — map it to a real translated state instead of
      // showing untranslatable raw text for what's a very common case
      // (backlog AUTH-1).
      const message = err instanceof Error ? err.message : "";
      if (/invalid login credentials/i.test(message)) {
        setSubmitError({ key: "auth.error.invalidCredentials" });
      } else {
        setSubmitError(err instanceof Error ? { raw: err.message } : { key: "auth.error.generic" });
      }
    } finally {
      setBusy(false);
    }
  }

  async function startOAuth(provider: "google" | "azure") {
    setSubmitError(null);
    // Busy immediately, not just for the password form's own submit
    // (backlog AUTH-16) — signInWithOAuth() doesn't navigate away
    // synchronously, so a double-click on either provider button
    // previously fired two calls before the redirect actually left the
    // page.
    setBusy(true);
    markOAuthRedirectStarted();
    // returnTo survives the full external redirect through the OAuth
    // provider (React Router state doesn't) — App.tsx's own effect
    // consumes it once a session exists.
    const redirectTo = from ? `${window.location.origin}/?returnTo=${encodeURIComponent(from)}` : `${window.location.origin}/`;
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });
    if (authError) {
      setSubmitError({ raw: authError.message });
      setBusy(false);
      clearOAuthRedirectMarker();
    }
  }

  return (
    <AuthLayout
      eyebrow={t("login.eyebrow")}
      footer={
        <p className="mt-6 text-center text-sm text-foreground/80">
          {t("login.newPrompt")}
          <Link to="/signup" className="ml-1.5 text-arc hover:text-spark">
            {t("login.createAccount")}
          </Link>
        </p>
      }
    >
      {linkExpired && (
        <div role="alert" className="mt-6 rounded-xl border border-arc/50 bg-black/30 p-5">
          <div className="flex items-center gap-3">
            <Link2Off aria-hidden className="h-5 w-5 shrink-0 text-arc" />
            <p className="font-display text-xl uppercase tracking-wide text-arc">{t("auth.expired.title")}</p>
          </div>
          <p className="mt-2 text-sm text-foreground/80">{t("auth.expired.body")}</p>
          <p className="mt-3 text-sm">
            <Link to="/signup" className="text-arc hover:text-spark">
              {t("auth.expired.signupAgain")}
            </Link>
            <span aria-hidden className="mx-2 text-muted-foreground">
              ·
            </span>
            <Link to="/forgot-password" className="text-arc hover:text-spark">
              {t("auth.expired.resetLink")}
            </Link>
          </p>
        </div>
      )}

      {oauthError && (
        <div role="alert" className="mt-6 rounded-xl border border-arc/50 bg-black/30 p-5">
          <div className="flex items-center gap-3">
            <ShieldAlert aria-hidden className="h-5 w-5 shrink-0 text-arc" />
            <p className="font-display text-xl uppercase tracking-wide text-arc">{t("auth.oauthError.title")}</p>
          </div>
          <p className="mt-2 text-sm text-foreground/80">{t("auth.oauthError.body")}</p>
        </div>
      )}

      {submitError && (
        <div className="mt-6">
          <FieldError>
            <span className="block">{resolveMessage(t, submitError)}</span>
            {/* Shown for every invalid-credentials failure alike (wrong
                password, unknown email, or a Google/Microsoft-only account
                with no password set) — never conditioned on the specific
                email or which case actually applies, so it can't be used to
                enumerate whether an account exists or which provider it
                uses (backlog AUTH-8 follow-up, 2026-09-04). */}
            {"key" in submitError && submitError.key === "auth.error.invalidCredentials" && (
              <span className="mt-1 block text-xs text-destructive/80">{t("login.notSureHint")}</span>
            )}
          </FieldError>
        </div>
      )}

      <SsoButtons
        disabled={busy}
        onGoogle={() => startOAuth("google")}
        onMicrosoft={() => startOAuth("azure")}
      />
      <AuthDivider />

      <form className="mt-6 space-y-4" onSubmit={handleSubmit} noValidate>
        <div className="space-y-2">
          <label className="relative flex items-center">
            <AtSign className="pointer-events-none absolute left-4 h-4 w-4 text-arc" />
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError("email");
              }}
              placeholder={t("login.email")}
              autoComplete="email"
              className={cn(authFieldClassName(!!errors.email), errors.email && "pr-10")}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "login-email-error" : undefined}
            />
          </label>
          {errors.email && <FieldError id="login-email-error">{t(errors.email)}</FieldError>}
        </div>

        <div className="space-y-2">
          <PasswordInput
            invalid={!!errors.password}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearError("password");
            }}
            placeholder={t("login.password")}
            autoComplete="current-password"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? "login-password-error" : undefined}
          />
          {errors.password && <FieldError id="login-password-error">{t(errors.password)}</FieldError>}
        </div>

        <div className="flex items-center justify-between pt-1">
          <label className="flex min-h-11 items-center gap-3 text-sm text-foreground/85">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-5 w-5 rounded-md border border-arc bg-transparent accent-[var(--sd-arc)]"
            />
            {t("login.keepSignedIn")}
          </label>
          <Link to="/forgot-password" className="text-sm text-arc hover:text-spark">
            {t("login.forgot")}
          </Link>
        </div>

        <button type="submit" disabled={busy} className={authPrimaryClass}>
          {busy ? "…" : t("login.submit")}
        </button>
      </form>
    </AuthLayout>
  );
}
